import React from 'react';

import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Messages } from '~src/codegen/Localize';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { IRemoteAgentConnectionStatus } from '~src/settings/bridge-settings/serverApi/requests/GetSiteRemoteAgentsConnectionStatusRequest';
import { ISiteAdminBridgeSettings, ISiteBridgeSettingsForSiteAdmin } from '~src/settings/types/ISiteAdminBridgeSettings';
import { Sites } from '~src/siteManagement/services/resources/Sites';
import { incrementRouterSpaceReactKey } from '~src/spacesPlatform/actions/IncrementRouterSpaceReactKeyAction';

import { SettingsFormikContainer } from '../components/common/SettingsFormikContainer';
import { EdgePoolsPayload } from './sections/types/EdgePoolsPayload';

// the only purpose of this type's existence is to map to
// the props(which is the same as the response body for the getbridge settings API call)
// that magically get sent to this component
export type SiteBridgeSettingsForSiteAdminProps = {
  settings: { // this is the same as the type of the response body  in the get bridge settings API response
    id?: string; // id shouldnt be in the settings passed on to settings
    siteAdminBridgeSettings?: ISiteAdminBridgeSettings;
  };
  connectionStatuses?: IRemoteAgentConnectionStatus[];
  edgePools?: EdgePoolsPayload;
};

export type BridgeSiteSettingsProps = SiteBridgeSettingsForSiteAdminProps & {
  refreshEdgePools: () => void;
  hasLoadedEdgePools: boolean;
};

const bridgeSiteSettingsSections = [
  'BridgeSettingsSummarySection',
  'BridgeClientNotRunningNotificationsSection',
  'BridgePoolingSection',
  'BridgeUnassignedRemoteAgentsSection',
  'BridgeDefaultRemoteAgentsSection',
  'BridgeNetworkAllowlist'
];


export function BridgeSiteSettings(props: BridgeSiteSettingsProps) {
  const submitHandler = useSubmitHandler(props);

  return <SettingsFormikContainer
    formName={'BridgeSiteSettings'} // this is required
    onSubmit={submitHandler} // submitHandler gets called when the save button is clicked.
    sections={bridgeSiteSettingsSections}
    {...props}
  />;
}

function useSubmitHandler(settings: SiteBridgeSettingsForSiteAdminProps) {
  return React.useCallback(async (newSiteAdminBridgeSettings: SiteBridgeSettingsForSiteAdminProps['settings']) => {
    const siteId = newSiteAdminBridgeSettings.id;

    const sitesService = Sites.instance;
    const result: ISiteAdminBridgeSettings | IResult = await sitesService.updateSiteBridgeSettingsForSiteAdmin(
      siteId, newSiteAdminBridgeSettings.siteAdminBridgeSettings as ISiteAdminBridgeSettings);

    if (result?.errors?.length) {
      throw result.errors.map((e: any) => sitesService.errorMessage(e.code, null));
    } else {
      /**
        * Have to use a hard reload here to force settings to update with newly saved values
        * TODO: This solution could be improved in the future
        */
      await getSessionManagementServiceInstance().refreshSessionInfo();
      getAppStore().dispatch(incrementRouterSpaceReactKey());

      ToasterService.getInstance().pop({ type: ToastType.Success, text: Messages.settingsUpdateSuccess() });

      // Return a snapshot of saved settings in case API call fails without error
      const savedSettings: ISiteBridgeSettingsForSiteAdmin = await sitesService.getSiteBridgeSettingsForSiteAdminById(siteId);
      return {
        settings: savedSettings,
        connectionStatuses: settings.connectionStatuses
      };
    }
  }, [settings]);
}
